import { Box, Drawer, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import { useEffect, useState, useRef, useCallback } from 'react';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import { PlayContainer } from 'Components/RoadmapComponents/PlayContainer';
import { Minimap } from 'Components/RoadmapComponents/Minimap';
import { Menu } from 'Components/RoadmapComponents/Menu';
import { Path } from 'Components/RoadmapComponents/Path';
import { useNavigate, useLocation } from 'react-router-dom';
import { MainAppReducer, UserLevelData } from 'Types';
import { getHighestCompletedLevel, getUnitPctComplete } from 'Utils/UserProgress';
import { CustomCircularProgress } from 'Components/StyledComponents';
import Survey from './Survey';
import './Roadmap.css';
import useMediaQuery from '@mui/material/useMediaQuery';
import { isCapacitorNative } from 'Utils/Constants';
import Confirmation from 'Containers/Payment/Confirmation';
import logger from 'Utils/Logger';
import MapIconSelected from '../../assets/images/MapIconSelected.png';
import RepertoireIcon from '../../assets/images/RepertoireIcon.png';
import HelpIcon from '../../assets/images/HelpIcon.png';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';

const smallMobileRoadmapQuery = '(max-width:480px), (max-height:480px)';

const Roadmap = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const auth = useAuthWrapperContext();

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const { levelData, levelSelect, userLevelData, tiersByLevels, unitSelect, userData } = data;

  // IMPORTANT: sane defaults (these are expected to be 0..1)
  const [areaToPreventScroll, setAreaToPreventScroll] = useState<string>('');
  const [scrollPos, setScrollPos] = useState<number>(0);
  const [scrollAreaSize, setScrollAreaSize] = useState<number>(0.5);

  const [pathScrollPos, setPathScrollPos] = useState(0);
  const roadmapRef = useRef<HTMLDivElement | null>(null);
  const latestLevelRef = useRef<HTMLDivElement | null>(null);

  const [cursorInRoadmap, setCursorInRoadmap] = useState(false);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryParam = params.get('show-thank-you');
    logger.info('queryParam:', queryParam);
    if (queryParam) setShowPaymentConfirmation(true);
  }, [location.search]);

  useEffect(() => {
    if (userData && userData.subscription_status == '') {
      navigate('/welcome');
    }
  }, [userData, navigate]);

  const [highestCompleteData, setHighestCompleteData] = useState(
    getHighestCompletedLevel(userLevelData),
  );
  useEffect(() => {
    setHighestCompleteData(getHighestCompletedLevel(userLevelData));
  }, [userLevelData]);

  const isSmallMobile = useMediaQuery(smallMobileRoadmapQuery);

  // Desktop-only: prevent trackpad/back-gesture weirdness. Skip on Capacitor.
  useEffect(() => {
    if (isCapacitorNative()) return;

    const preventNavigation = (e: WheelEvent) => {
      if (roadmapRef.current && !cursorInRoadmap) e.preventDefault();
    };

    const el = roadmapRef.current;
    if (el) {
      el.addEventListener('wheel', preventNavigation as EventListener, { passive: false });
      return () => el.removeEventListener('wheel', preventNavigation as EventListener);
    }
  }, [cursorInRoadmap]);

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!latestLevelRef.current) return;
    const boxRect = latestLevelRef.current.getBoundingClientRect();
    const isInside =
      e.clientX >= boxRect.left &&
      e.clientX <= boxRect.right &&
      e.clientY >= boxRect.top &&
      e.clientY <= boxRect.bottom;

    setCursorInRoadmap(isInside);
  };

  const handleUnitSelect = useCallback(
    (unit: number) => {
      dispatch(appActions.setUnitSelect({ unitSelect: unit }));
    },
    [dispatch],
  );

  const navMenu = useCallback(
    (path: string) => {
      setMobileNavOpen(false);
      if (path === 'faq') {
        window.open('https://museflow.ai/faq', '_blank');
      } else {
        navigate(`/${path}`);
      }
    },
    [navigate],
  );

  const logOut = useCallback(() => {
    setMobileNavOpen(false);
    auth?.logOut();
  }, [auth]);

  const heightMatchPlayContainer = useMediaQuery('(min-height:600px)');

  const selectedLevel = levelData[levelSelect] || levelData[0];
  const level_number = selectedLevel ? selectedLevel.level_number : 0;
  const name = selectedLevel ? selectedLevel.name : '';
  const description = selectedLevel?.description;

  const lastLevelComplete = highestCompleteData.lastLevelComplete;
  const userLevelStatuses = highestCompleteData.userLevelStatuses;
  const userLevelTiersComplete = highestCompleteData.userLevelTiersComplete;

  let selectedLevelProgress: UserLevelData | undefined;
  userLevelData.forEach((ul) => {
    if (ul?.level?.level_number == level_number) selectedLevelProgress = ul;
  });

  const unitPercentComplete = getUnitPctComplete(lastLevelComplete, levelData);
  const tierByLevel = tiersByLevels
    ? tiersByLevels.find((item) => item.level_number === level_number)
    : undefined;
  const tierCountForLevel = tierByLevel?.tiers?.length || 1;

  if (!levelData || levelData.length < 2) {
    return (
      <Box>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CustomCircularProgress />
        </Box>
      </Box>
    );
  }

  const isCap = isCapacitorNative();

  return (
    <Box
      sx={{ display: 'flex', height: '100vh', width: '100%' }}
      ref={roadmapRef}
      onPointerMove={handlePointerMove}
    >
      {!isSmallMobile && <Menu selected={'Roadmap'} />}
      {isSmallMobile && (
        <>
          <IconButton
            onClick={() => setMobileNavOpen(true)}
            sx={{
              position: 'fixed',
              zIndex: 1200,
              top: 'calc(env(safe-area-inset-top) + 17px)',
              left: 'calc(env(safe-area-inset-left) + 18px)',
              color: '#CED0D4',
              backgroundColor: 'rgba(7, 20, 35, 0.6)',
              '&:hover': {
                backgroundColor: 'rgba(7, 20, 35, 0.85)',
              },
            }}
            aria-label="Open menu"
          >
            <MenuRoundedIcon />
          </IconButton>

          <Drawer
            anchor="left"
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: '162px',
                minWidth: '162px',
                maxWidth: '162px',
                backgroundColor: '#1D2F44',
                color: '#CED0D4',
                paddingTop: 'max(12px, env(safe-area-inset-top))',
                paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
                display: 'flex',
                alignItems: 'stretch',
              },
            }}
          >
            <Box
              sx={{
                width: '108px',
                marginLeft: 'auto',
                marginRight: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pb: 1,
              }}
            >
              <Box
                onClick={() => navMenu('roadmap')}
                sx={{
                  marginTop: '14px',
                  borderRadius: 1,
                  opacity: 0.9,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                <img src={MapIconSelected} style={{ width: '22px' }} />
                <Box
                  className="menu-text-styles"
                  sx={{ marginTop: '8px', fontSize: '13px', lineHeight: '16px' }}
                >
                  Roadmap
                </Box>
              </Box>

              <Box
                onClick={() => navMenu('repertoire')}
                sx={{
                  marginTop: '20px',
                  borderRadius: 1,
                  opacity: 0.85,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                <img src={RepertoireIcon} style={{ width: '22px' }} />
                <Box
                  className="menu-text-styles"
                  sx={{ marginTop: '8px', fontSize: '13px', lineHeight: '16px' }}
                >
                  Repertoire
                </Box>
              </Box>

              <Box
                onClick={() => navMenu('dashboard')}
                sx={{
                  marginTop: '20px',
                  borderRadius: 1,
                  opacity: 0.85,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                <Box
                  sx={{
                    borderRadius: '20px',
                    width: '40px',
                    fontWeight: '600',
                    fontSize: '9pt',
                    textAlign: 'center',
                    background: '#28856F',
                    color: '#fff',
                    marginBottom: '5px',
                  }}
                >
                  NEW
                </Box>
                <svg
                  className="menu-img-styles"
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="9" stroke="#ADB1B7" strokeWidth="1.6" />
                  <path
                    d="M12 12L16 8"
                    stroke="#ADB1B7"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path d="M12 12V6" stroke="#ADB1B7" strokeWidth="1" strokeLinecap="round" />
                </svg>
                <Box
                  className="menu-text-styles"
                  sx={{ marginTop: '8px', fontSize: '13px', lineHeight: '16px' }}
                >
                  Dashboard
                </Box>
              </Box>

              <Box
                onClick={() => navMenu('settings')}
                sx={{
                  marginTop: '20px',
                  borderRadius: 1,
                  opacity: 0.85,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                <PersonIcon sx={{ color: '#ADB1B7', fontSize: '30px' }} />
                <Box
                  className="menu-text-styles"
                  sx={{ marginTop: '8px', fontSize: '13px', lineHeight: '16px' }}
                >
                  Account
                </Box>
              </Box>

              <Box
                onClick={() => navMenu('faq')}
                sx={{
                  marginTop: '20px',
                  borderRadius: 1,
                  opacity: 0.85,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                <img src={HelpIcon} style={{ width: '22px' }} />
                <Box
                  className="menu-text-styles"
                  sx={{ marginTop: '8px', fontSize: '13px', lineHeight: '16px' }}
                >
                  Help
                </Box>
              </Box>

              <Box
                onClick={logOut}
                sx={{
                  marginTop: '20px',
                  borderRadius: 1,
                  opacity: 0.85,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  userSelect: 'none',
                }}
              >
                <LogoutIcon
                  sx={{ color: 'rgb(255,255,255,0.8)', fontSize: '24px', transform: 'scaleX(-1)' }}
                />
                <Box
                  className="menu-text-styles"
                  sx={{ marginTop: '8px', fontSize: '13px', lineHeight: '16px' }}
                >
                  Log Out
                </Box>
              </Box>
            </Box>
          </Drawer>
        </>
      )}
      <Survey />

      {showPaymentConfirmation && <Confirmation setShowConfirmation={setShowPaymentConfirmation} />}

      <Box
        sx={{
          width: isSmallMobile ? '100%' : 'calc(100% - var(--app-sidebar-width, 100px))',
          backgroundColor: 'black',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            background: 'linear-gradient(0deg, black, #1D2F44)',
            height: '100%',
          }}
        >
          <Box className="header-container">
            <Box className="roadmap-title-container">Roadmap</Box>
            <Box className="minimap-container">
              <Minimap
                unitPercentComplete={unitPercentComplete}
                scrollPosFromRoadmap={scrollPos}
                setScrollPosFromRoadmap={setScrollPos}
                scrollAreaSize={scrollAreaSize}
                areaToPreventScroll={areaToPreventScroll}
                setAreaToPreventScroll={setAreaToPreventScroll}
                setPathScrollPos={setPathScrollPos}
              />
            </Box>
          </Box>

          {!isCap &&
            (process.env.REACT_APP_NODE_ENV === 'local' ||
              process.env.REACT_APP_NODE_ENV === 'dev') && (
              <Box
                sx={{
                  alignSelf: 'baseline',
                  color: 'white',
                  backgroundColor: '#1D2F44',
                  marginLeft: '50px',
                }}
              >
                <Box sx={{ minWidth: 120 }}>
                  <FormControl fullWidth sx={{ color: '#fff', svg: { fill: '#fff' } }}>
                    <InputLabel id="unit-select-label" sx={{ color: '#fff' }}>
                      Unit
                    </InputLabel>
                    <Select
                      labelId="unit-select-label"
                      id="unit-select"
                      value={unitSelect.toString()}
                      label="Unit"
                      onChange={(evt: SelectChangeEvent) =>
                        handleUnitSelect(parseInt(evt.target.value))
                      }
                      sx={{ color: '#fff', svg: { fill: '#fff' } }}
                    >
                      <MenuItem value={1}>1</MenuItem>
                      <MenuItem value={2}>2</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            )}

          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Box className="roadmap-container-styles">
              <Path
                levelSelect={levelSelect}
                unitSelect={unitSelect}
                levelData={levelData}
                userLevelData={userLevelData}
                lastLevelComplete={lastLevelComplete}
                userLevelStatuses={userLevelStatuses}
                userLevelTiersComplete={userLevelTiersComplete}
                scrollPos={scrollPos}
                setScrollPos={setScrollPos}
                setScrollAreaSize={setScrollAreaSize}
                areaToPreventScroll={areaToPreventScroll}
                setAreaToPreventScroll={setAreaToPreventScroll}
                pathScrollPos={pathScrollPos}
                latestLevelRef={latestLevelRef}
                isSmallMobile={isSmallMobile}
              />
            </Box>

            {!isSmallMobile && (!isCap || heightMatchPlayContainer) && (
              <PlayContainer
                selectedLevel={selectedLevel}
                levelSelect={levelSelect}
                level_number={level_number}
                name={name}
                description={description}
                selectedLevelProgress={selectedLevelProgress}
                tierCountForLevel={tierCountForLevel}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Roadmap;
